// Pure helpers for the depth card. No React, no fetch — unit-tested with
// node:test (mirrors lib/spread.ts). The card stays a thin renderer over these.
import type { DepthBucketRow } from "../api/types.ts";

// reconstruction_status -> chip label + color + plain-language reason.
// Mirrors the SpreadCaptureCard label-table idiom. GREEN = trust the number;
// AMBER/MUTED = shown greyed with the reason, never hidden.
const GREEN = "#6fcf97";
const AMBER = "#e0a458";
const MUTED = "#8a93a6";

export interface StatusMeta {
  label: string;
  color: string;
  reason: string;
  ok: boolean; // ok === true means values render at full strength
}

const STATUS: Record<string, StatusMeta> = {
  ok: {
    label: "ok",
    color: GREEN,
    reason: "Fresh anchor; all bands measurable.",
    ok: true,
  },
  feed_limited: {
    label: "feed-limited",
    color: AMBER,
    reason: "A depth band did not reach its edge this bucket.",
    ok: false,
  },
  stale_anchor: {
    label: "stale anchor",
    color: AMBER,
    reason: "Snapshot too old to certify depth; values shown for reference.",
    ok: false,
  },
  insufficient_book: {
    label: "thin book",
    color: MUTED,
    reason: "Too few levels near touch to certify depth.",
    ok: false,
  },
  no_data: {
    label: "no data",
    color: MUTED,
    reason: "No reconstructable book in this bucket.",
    ok: false,
  },
};

export function statusMeta(status: string | null | undefined): StatusMeta {
  return STATUS[status ?? "no_data"] ?? STATUS.no_data;
}

// Rows arrive ascending by bucket_start (the adapter reverses for charts); the
// card shows the most recent, i.e. the last element. Null if empty.
export function latestBucket(
  rows: DepthBucketRow[] | undefined | null,
): DepthBucketRow | null {
  if (!rows || rows.length === 0) return null;
  return rows[rows.length - 1];
}

// One side+band cell for the card. value is quote notional (primary). null when
// the band was not measurable (distinct from a measured zero).
export interface BandCell {
  measurable: boolean;
  notionalQuote: number | null;
  sizeBase: number | null;
  levels: number | null;
}

const BANDS = ["0p5", "1p0", "2p0"] as const;
export type BandKey = (typeof BANDS)[number];
export const BAND_LABELS: Record<BandKey, string> = {
  "0p5": "±0.5%",
  "1p0": "±1%",
  "2p0": "±2%",
};

export function bandCell(
  row: DepthBucketRow,
  side: "bid" | "ask",
  band: BandKey,
): BandCell {
  // index via the schema column names, keeping unit suffixes explicit
  const r = row as unknown as Record<string, number | boolean | null>;
  return {
    measurable: Boolean(r[`${side}_measurable_${band}`]),
    notionalQuote: (r[`${side}_notional_${band}_quote`] as number | null) ?? null,
    sizeBase: (r[`${side}_size_${band}_base`] as number | null) ?? null,
    levels: (r[`${side}_levels_${band}`] as number | null) ?? null,
  };
}

// Quote-notional formatting: compact currency-ish. null -> em dash (not zero).
export function fmtNotional(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "k";
  return n.toFixed(0);
}

export function fmtBase(n: number | null | undefined): string {
  return n == null ? "—" : n.toLocaleString();
}

// "5m ago" style relative age from a bucket_start ISO string, vs a reference.
export function bucketAgeLabel(isoBucketStart: string, nowMs: number): string {
  const diffMs = nowMs - Date.parse(isoBucketStart);
  if (Number.isNaN(diffMs)) return "";
  const mins = Math.max(0, Math.round(diffMs / 60_000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m ago`;
}
